import { refreshTokenMutation } from '@/mutations/auth/refreshTokenMutation';
import axios, { type InternalAxiosRequestConfig } from 'axios';
import type { Client } from '../../__generated__/openapi-types';
import { SuperDutyError } from './errors';

const hardLogout = () => {
  localStorage.removeItem('user');
  localStorage.removeItem('accessToken');
  localStorage.removeItem('refreshToken');
  window.location.reload();
};

export const addAuthInterceptor = (api: Client) => {
  api.interceptors.request.use(async (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('accessToken');

    if (token && token !== 'null') {
      config.headers = config.headers || {};
      config.headers['Authorization'] = `Bearer ${JSON.parse(token)}`;
    }

    return config;
  });
};

export const addErrorInterceptor = (api: Client) => {
  api.interceptors.response.use(
    (response) => response,
    async (error) => {
      if (axios.isAxiosError(error)) {
        const originalRequest = error.config;

        if (
          error.response?.status === 401 &&
          error.response.data.code === 'INVALID_TOKEN' &&
          originalRequest
        ) {
          const refreshTokenInLocalStorage =
            localStorage.getItem('refreshToken');

          if (!refreshTokenInLocalStorage) {
            hardLogout();
            return;
          }

          try {
            const { accessToken, refreshToken } =
              await refreshTokenMutation().mutationFn({
                refreshToken: JSON.parse(refreshTokenInLocalStorage),
              });

            localStorage.setItem('accessToken', JSON.stringify(accessToken));
            localStorage.setItem('refreshToken', JSON.stringify(refreshToken));

            // Update the failed request with new token and retry
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${accessToken}`;
            }

            return api(originalRequest);
          } catch (refreshError) {
            hardLogout();
          }
        }

        if (error.response?.data?.code) {
          throw new SuperDutyError(
            error.response?.status,
            error.response?.data?.error,
            error.response?.data?.code,
            error.response?.data?.message,
            error.response?.data?.metadata
          );
        }
      }

      throw new SuperDutyError(
        error.response?.status,
        error.response?.data?.error ?? 'UNKNOWN_ERROR',
        error.response?.data?.code ?? 'UNKNOWN_ERROR',
        error.response?.data?.message ?? 'UNKNOWN_ERROR',
        {}
      );
    }
  );
};
